import clsx from 'clsx';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import s from './Card.module.scss';

type CardPadding = 'none' | 'sm' | 'md' | 'lg';
type CardElement = 'div' | 'section' | 'article';
type CardEmphasis = 'hero' | 'default' | 'quiet';

export interface CardProps {
  children: ReactNode;
  className?: string;
  /** 'none' = 0, 'sm' = 16px, 'md' = 24px (default), 'lg' = 32px */
  padding?: CardPadding;
  /**
   * Visual weight (W3.6). 'hero' = gradient-accent border + lift, for the one
   * or two cards that matter most on a screen; 'quiet' = flat, no blur, for
   * secondary/supporting cards; 'default' = the standard glass card.
   */
  emphasis?: CardEmphasis;
  /** When set, the card becomes interactive (hover state + focus ring). */
  onClick?: () => void;
  /**
   * Destination for a card that navigates (P3/A6, 4.1.2 + 2.4.4). Prefer this
   * over `onClick` whenever the card's job is "go here": it renders a real
   * anchor, so the card announces as a link with a destination, and gets
   * middle-click / open-in-new-tab / status-bar preview for free. A
   * `div[role="button"]` announces as a button and offers none of that.
   */
  to?: string;
  /**
   * Router state to carry along with `to`. For a destination whose own screen
   * cannot look its title up — the Library's built-in folders are created lazily,
   * so fetching one by id before anything is filed into it 404s — this is how the
   * label reaches the breadcrumb. Ignored without `to`.
   */
  state?: unknown;
  /** Accessible name for a navigational card whose content is not self-describing. */
  ariaLabel?: string;
  /** For an `onClick` card that is one of a set of choices: announces it as picked. */
  ariaPressed?: boolean;
  /** Anchors the card so something elsewhere on the page can scroll to it. */
  id?: string;
  as?: CardElement;
  /**
   * The card's box, and only its box — width in a gallery, a grid row span. The card
   * owns its own fill, edge and padding; setting those here is a fork in disguise.
   */
  style?: CSSProperties;
}

const paddingClass: Record<CardPadding, string | undefined> = {
  none: undefined,
  sm: s.padSm,
  md: s.padMd,
  lg: s.padLg,
};

const emphasisClass: Record<CardEmphasis, string | undefined> = {
  hero: s.hero,
  default: undefined,
  quiet: s.quiet,
};

export const Card = ({
  children,
  className,
  padding = 'md',
  emphasis = 'default',
  onClick,
  to,
  state,
  ariaLabel,
  ariaPressed,
  id,
  as: Tag = 'div',
  style,
}: CardProps) => {
  const interactive = to !== undefined || onClick !== undefined;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  const classes = clsx(
    s.card,
    paddingClass[padding],
    emphasisClass[emphasis],
    interactive && s.interactive,
    className,
  );

  if (to !== undefined) {
    return (
      <Link
        to={to}
        state={state}
        id={id}
        className={classes}
        style={style}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  }

  return (
    <Tag
      id={id}
      className={classes}
      style={style}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
    >
      {children}
    </Tag>
  );
};
